<template>
  <div class="pickup-store-filter">
    <button
      v-if="isSelected || hasEligibleLocations"
      type="button"
      class="pickup-store-filter__badge js-pickup-store-filter-badge"
      :class="{ 'is-selected': isSelected }"
      @click="handleBadgeClick"
    >
      <span
        class="pickup-store-filter__badge-icon icon icon--location"
        aria-hidden="true"
      />
      <span class="pickup-store-filter__badge-text">Same Day Pickup</span>
      <button
        v-if="isSelected"
        type="button"
        class="pickup-store-filter__badge-close"
        aria-label="Clear store filter"
        @click.stop="clearStore"
      >
        <span
          class="icon icon--close"
          aria-hidden="true"
        />
      </button>
    </button>
    <template v-if="isSelected && selectedStore">
      <button
        type="button"
        class="pickup-store-filter__store-info btn-link"
        @click="openStoreSelector"
      >
        <span class="pickup-store-filter__ready-text">Ready in 2-3 hours at </span>
        <span class="pickup-store-filter__store-name">{{ selectedStoreName }}</span>
      </button>
    </template>
  </div>
</template>

<script>
import { ref, computed, onMounted, onUnmounted } from "vue";
import storeLocationFilter from "../../lib/store-location-filter";
import trapFocus from "../../lib/trap-focus";
import { getCollectionPickupLocationIds, isStoreEligibleForPickup } from "../../lib/pickup-eligibility";

export default {
  name: "PickupStoreFilter",
  setup() {
    const selectedStore = ref(null);
    const isSelected = ref(false);
    // Whether the current collection/filter set has any same-day pickup location.
    // Starts true so the badge never flashes hidden before the first grid read.
    const hasEligibleLocations = ref(true);
    let storeSelectorCloseObserver = null;

    const selectedStoreName = computed(() => {
      if (!selectedStore.value) {
        return "";
      }
      return selectedStore.value.name || "";
    });

    function updateStore() {
      const store = storeLocationFilter.getSelectedStore();
      selectedStore.value = store;
    }

    // Hide the badge when the current grid has zero pickup-eligible locations, so
    // customers can't activate Same Day Pickup into a guaranteed-blank result.
    // null = unknown (no grid) → keep visible; empty Set → hide; non-empty → show.
    function refreshEligibility() {
      const eligibleIds = getCollectionPickupLocationIds();
      hasEligibleLocations.value = eligibleIds === null || eligibleIds.size > 0;
    }

    function handleStoreLocationChanged() {
      updateStore();
    }

    function openStoreSelector() {
      // Trigger the existing store selector modal
      const $body = document.body;
      const $storeSelectorModal = document.getElementById("storeSelectorModal");

      if (!$storeSelectorModal) {
        console.warn("Store selector modal not found");
        return;
      }

      __.addClass($body, "is-store-selector-exposed");
      $storeSelectorModal.setAttribute("aria-hidden", "false");

      setTimeout(() => {
        trapFocus($storeSelectorModal);
      }, 150);
    }

    async function handleBadgeClick() {
      if (isSelected.value) return;

      isSelected.value = true;
      window.dispatchEvent(new CustomEvent("quickShipDeactivated"));
      window.dispatchEvent(new CustomEvent("pickupBadgeActivated"));

      // Locations that have pickup-eligible products in this collection.
      // null = unknown (no grid) → treat every store as eligible (no over-filtering).
      const eligibleIds = getCollectionPickupLocationIds();

      // 1. Reuse a previously selected store ONLY if it's eligible for this
      //    collection — a stale selection must never blank the page.
      const store = storeLocationFilter.getStoreFromStorage();

      if (store && isStoreEligibleForPickup(store, eligibleIds)) {
        storeLocationFilter.setSelectedStore(store);
        updateStore();
        return;
      }

      // 2. Auto-select the nearest store that's eligible for this collection.
      const nearestStores = await storeLocationFilter.getNearestStores();
      const nearestEligible = (nearestStores || []).find((candidate) =>
        isStoreEligibleForPickup(candidate, eligibleIds)
      );

      if (nearestEligible) {
        storeLocationFilter.setSelectedStore(nearestEligible);
        updateStore();
        return;
      }

      // 3. No eligible store known — prompt the user with the (filtered) picker.
      openStoreSelector();
    }

    function clearStore(event) {
      event?.stopPropagation?.();
      isSelected.value = false;
      setTimeout(() => {
        window.dispatchEvent(new CustomEvent("productsLoaded"));
      }, 100);
      window.dispatchEvent(new CustomEvent("pickupBadgeDeactivated"));
      window.dispatchEvent(new CustomEvent("quickShipActivated"));
    }

    function setupStoreSelectorCloseObserver() {
      const $body = document.body;
      const observer = new MutationObserver(() => {
        if (!$body.classList.contains("is-store-selector-exposed")) {
          if (isSelected.value && !selectedStore.value) {
            isSelected.value = false;
          }
        }
      });
      observer.observe($body, {
        attributes: true,
        attributeFilter: ["class"]
      });
      return observer;
    }

    function handleClearAllAvailabilityFilters() {
      isSelected.value = false;
    }

    onMounted(() => {
      clearStore({ stopPropagation: () => true });
      refreshEligibility();
      window.addEventListener("storeLocationChanged", handleStoreLocationChanged);
      window.addEventListener("clearAllAvailabilityFilters", handleClearAllAvailabilityFilters);
      window.addEventListener("productsLoaded", refreshEligibility);

      // Listen for store selection from StoreSelector component
      document.addEventListener("storeSelected", (event) => {
        if (event.detail && event.detail.store) {
          storeLocationFilter.setSelectedStore(event.detail.store);
          updateStore();
        }
      });
      storeSelectorCloseObserver = setupStoreSelectorCloseObserver();
    });

    onUnmounted(() => {
      window.removeEventListener("storeLocationChanged", handleStoreLocationChanged);
      window.removeEventListener("clearAllAvailabilityFilters", handleClearAllAvailabilityFilters);
      window.removeEventListener("productsLoaded", refreshEligibility);
      storeSelectorCloseObserver?.disconnect();
    });

    return {
      selectedStore,
      selectedStoreName,
      isSelected,
      hasEligibleLocations,
      openStoreSelector,
      handleBadgeClick,
      clearStore
    };
  }
};
</script>
